/* eslint-disable max-lines */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Tag as TagIcon,
  ExternalLink,
  X,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import {
  useGetTagAssignments,
  useDeleteTagAssignment
} from '@utils/api/tag-assignments';
import { TagAssignment } from '@utils/types/organization/tag.ts';
import { groupAssignmentsByTag } from '@utils/tags/tag-helpers.ts';
import { InfoTooltip } from '@components/atoms/ui-kit/info-tooltip.tsx';
import { SkeletonBar } from '@components/atoms/ui-kit/skeleton';
import { PATHS } from '../../router/paths.ts';

type Props = {
  sessionId: string;
  documentId: string;
};

// Above this many rows we collapse the assignments list behind a disclosure
// so the export modal doesn't grow unbounded with a long tag history.
const ASSIGNMENTS_COLLAPSE_THRESHOLD = 5;

// Above this many distinct tags we truncate the chip row and add "+N more".
const CHIPS_TRUNCATE_AT = 6;

export const DocumentTagSummary = ({ sessionId, documentId }: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const { data, isLoading } = useGetTagAssignments({
    session_id: sessionId,
    document_id: documentId
  });
  const deleteAssignment = useDeleteTagAssignment();

  const grouped = useMemo(
    () => groupAssignmentsByTag((data?.result ?? []) as TagAssignment[]),
    [data]
  );

  const totalAssignments = data?.result?.length ?? 0;

  const [activeFilterId, setActiveFilterId] = useState<number | null>(null);
  const [chipsExpanded, setChipsExpanded] = useState(false);
  // Auto-collapse when the list is long; user can expand.
  const [listExpanded, setListExpanded] = useState(
    totalAssignments <= ASSIGNMENTS_COLLAPSE_THRESHOLD
  );

  const visibleGroups = useMemo(() => {
    if (activeFilterId == null) return grouped;
    return grouped.filter(g => g.tagId === activeFilterId);
  }, [grouped, activeFilterId]);

  const visibleCount = visibleGroups.reduce(
    (n, g) => n + g.assignments.length,
    0
  );

  const displayedChips =
    chipsExpanded || grouped.length <= CHIPS_TRUNCATE_AT
      ? grouped
      : grouped.slice(0, CHIPS_TRUNCATE_AT);
  const hiddenChipCount = grouped.length - displayedChips.length;

  const handleRemove = (assignment: TagAssignment) => {
    deleteAssignment.mutate(
      {
        session_id: sessionId,
        document_id: documentId,
        assignment_id: String(assignment.id)
      },
      {
        onSuccess: () => {
          toast.success(t('tag-removed') || 'Tag removed');
        },
        onError: () => {
          toast.error(t('failed-to-remove-tag') || 'Failed to remove tag');
        }
      }
    );
  };

  return (
    <div
      className="rounded-2xl border px-4 py-3"
      style={{
        borderColor: getColour('border'),
        backgroundColor: getColour('thirdLayer')
      }}
    >
      <div className="flex items-center gap-2 mb-2">
        <TagIcon
          className="h-4 w-4"
          style={{ color: getColour('activeText') }}
        />
        <h4
          className="text-sm font-medium"
          style={{ color: getColour('normalText') }}
        >
          {t('tagged-sections') || 'Tagged sections'}
        </h4>
        <InfoTooltip
          content={
            t('tagged-sections-info-tooltip') ||
            'Tagged sections are grouped by tag and inserted at the {Tagged Sections} placeholder in your template, or appended at the end of the export if no placeholder exists.'
          }
        />
        {grouped.length > 0 && (
          <span
            className="text-xs ml-auto"
            style={{ color: getColour('inactiveText') }}
          >
            {totalAssignments}{' '}
            {totalAssignments === 1
              ? t('selection') || 'selection'
              : t('selections') || 'selections'}
          </span>
        )}
      </div>

      {/* One-line summary; longer detail lives in the InfoTooltip above. */}
      <p className="text-xs mb-3" style={{ color: getColour('inactiveText') }}>
        {t('tagged-sections-short-hint') ||
          'These highlighted sections will appear grouped by tag in your DOCX export.'}
      </p>

      {isLoading ? (
        <SkeletonBar className="h-3 w-2/3 mt-1" />
      ) : grouped.length === 0 ? (
        <div
          className="flex items-center justify-between gap-2 text-xs"
          style={{ color: getColour('inactiveText') }}
        >
          <span>
            {t('no-tagged-sections') ||
              'No tagged sections in this document yet.'}
          </span>
          <Link
            to={PATHS().TAGS}
            className="inline-flex items-center gap-1 text-primary hover:underline"
          >
            <ExternalLink className="h-3 w-3" />
            {t('manage-tags') || 'Manage tags'}
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Tag chips. Click a chip to filter the list to just that tag; click
              again to clear. Truncates past CHIPS_TRUNCATE_AT with "+N more". */}
          <div className="flex flex-wrap gap-1.5 items-center">
            {displayedChips.map(g => {
              const isActive = activeFilterId === g.tagId;
              return (
                <button
                  key={g.tagId}
                  type="button"
                  onClick={() => setActiveFilterId(isActive ? null : g.tagId)}
                  className="inline-flex items-center gap-1.5 rounded-2xl px-2 py-1 text-xs font-medium border transition-colors"
                  style={{
                    borderColor: isActive
                      ? getColour('activeText')
                      : getColour('border'),
                    backgroundColor: isActive
                      ? getColour('appSoft')
                      : getColour('secondLayer'),
                    color: getColour('normalText')
                  }}
                  aria-pressed={isActive}
                  title={
                    isActive
                      ? t('clear-filter') || 'Clear filter'
                      : t('filter-by-this-tag') || 'Filter to this tag'
                  }
                >
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full"
                    style={{
                      backgroundColor: g.color || getColour('inactiveText'),
                      border: `1px solid ${getColour('border')}`
                    }}
                  />
                  {g.name}
                  <span
                    className="rounded-2xl px-1.5 text-[10px]"
                    style={{
                      backgroundColor: getColour('thirdLayer'),
                      color: getColour('inactiveText')
                    }}
                  >
                    {g.count}
                  </span>
                </button>
              );
            })}
            {hiddenChipCount > 0 && (
              <button
                type="button"
                onClick={() => setChipsExpanded(true)}
                className="text-xs px-2 py-1 rounded-2xl hover:underline"
                style={{ color: getColour('activeText') }}
              >
                {t('plus-n-more', { count: hiddenChipCount }) ||
                  `+${hiddenChipCount} more`}
              </button>
            )}
            {activeFilterId != null && (
              <button
                type="button"
                onClick={() => setActiveFilterId(null)}
                className="text-xs px-2 py-1 rounded-2xl hover:underline"
                style={{ color: getColour('inactiveText') }}
              >
                {t('clear-filter') || 'Clear filter'}
              </button>
            )}
          </div>

          {/* Assignments list — collapsed by default when many rows. Once
              expanded, capped by max-height + overflow-y-auto so the modal
              body stays scrollable rather than growing indefinitely. */}
          <div>
            <button
              type="button"
              onClick={() => setListExpanded(v => !v)}
              className="flex items-center gap-1 text-xs mb-1.5 hover:underline"
              style={{ color: getColour('inactiveText') }}
              aria-expanded={listExpanded}
            >
              {listExpanded ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              {listExpanded
                ? t('hide-details') || 'Hide details'
                : t('show-n-tagged-sections', { count: visibleCount }) ||
                  `Show ${visibleCount} tagged sections`}
            </button>
            {listExpanded && (
              <ul className="flex flex-col gap-y-0.5 max-h-48 overflow-y-auto">
                {visibleGroups.flatMap(g =>
                  g.assignments.map((a, idx) => (
                    <li
                      key={a.id}
                      className="flex items-center gap-2 px-2 py-1.5"
                      style={
                        idx === 0
                          ? undefined
                          : {
                              borderTop: `1px solid ${getColour('hairline')}`
                            }
                      }
                    >
                      <span
                        className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor:
                            a.tag_color || getColour('inactiveText'),
                          border: `1px solid ${getColour('border')}`
                        }}
                      />
                      <span
                        className="text-xs font-medium flex-shrink-0"
                        style={{ color: getColour('normalText') }}
                      >
                        {a.tag_name}
                      </span>
                      <span
                        className="text-xs italic truncate flex-1 min-w-0"
                        style={{ color: getColour('inactiveText') }}
                        title={a.selected_text}
                      >
                        &ldquo;{a.selected_text}&rdquo;
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemove(a)}
                        disabled={deleteAssignment.isLoading}
                        className="flex-shrink-0 p-1 rounded-md hover:bg-primary/5 transition-colors disabled:opacity-50"
                        aria-label={t('remove-tag') || 'Remove tag'}
                        title={t('remove-tag') || 'Remove tag'}
                      >
                        <X
                          className="h-3 w-3"
                          style={{ color: getColour('inactiveText') }}
                        />
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentTagSummary;
